import cx from 'classnames'
import type React from 'react'
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react'

import { clsJoin } from '@/utils'
import useClickOutside from '../../hooks/useClickOutside'
import IconInputCheck from '../../icons/IconInputCheck'
import { Action, Placeholder } from './components'
import css from './Input.module.scss'
import type { Classnames, InputType, Props } from './types'

const Input: React.ForwardRefExoticComponent<
  React.PropsWithoutRef<Props & InputType> & React.RefAttributes<{}>
> = forwardRef(
  (
    {
      can,
      classnames,
      disabled,
      error,
      errorHighlight = false,
      htmlType = 'text',
      icon = false,
      label,
      onChange,
      placeholder,
      size = 'large',
      success,
      type = 'default',
      initialValue = '',
      clearAfterApply,
      onClear,
      onSubmit,
      focusOnRender = false,
      buttonRight,
      ...props
    },
    ref
  ) => {
    const [active, setActive] = useState(false)
    const [value, setValue] = useState(initialValue)

    const inputRef = useRef<HTMLInputElement>(null)
    const clickOutside = useClickOutside<HTMLDivElement>(
      active,
      () => value === '' && onFocusHandle(false)
    )

    const classes: Classnames = {
      container: cx(
        css.container,
        disabled && css.disabled,
        size && css[size],
        classnames?.container
      ),
      input: cx(
        css.input,
        {
          [css.canClear]: can === 'clear' && value !== '',
          [css.success]: success,
          [css.error]: error || errorHighlight,
        },
        classnames?.input
      ),
      placeholder: classnames?.placeholder,
    }

    useEffect(() => {
      // @TODO refactor there is over complicated logic with refs and focus
      if (initialValue || focusOnRender) {
        onFocusHandle()
      }
    }, [])

    useEffect(() => {
      setValue(initialValue)
    }, [initialValue])

    useEffect(() => {
      if (active && value === '') {
        inputRef.current?.focus()
      }
    }, [active])

    const onFocusHandle = async (val?: boolean) => {
      await setActive(val ?? true)

      if (val) {
        inputRef.current?.focus()
      }
      if (!val) {
        inputRef.current?.blur()
      }
    }

    const onChangeHandle = (val: string) => {
      setValue(val)

      onChange?.(val)
    }

    const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.keyCode === 13 && value !== '' && onSubmit) {
        e.preventDefault()
        onSubmit(value)

        if (clearAfterApply) {
          setValue('')
        }
      }
    }

    useImperativeHandle(ref, () => ({
      focus: () => onFocusHandle(true),
      blur: () => onFocusHandle(false),
    }))

    if (type === 'default' && !active && !value) {
      return (
        <div className={classes.container}>
          {label && (
            <div className={css.label} onClick={() => onFocusHandle(true)}>
              {label}
            </div>
          )}

          <Placeholder
            className={classes.placeholder}
            disabled={disabled}
            onClick={onFocusHandle}
            size={size}
          >
            {placeholder}
          </Placeholder>
        </div>
      )
    }

    if (disabled) {
      return (
        <div className={classes.container}>
          {label && (
            <div className={css.label} onClick={() => onFocusHandle(true)}>
              {label}
            </div>
          )}

          <div className={classes.input}>
            <input
              {...props}
              type={htmlType}
              value={value}
              ref={inputRef}
              placeholder={type === 'normal' ? placeholder : ''}
            />
          </div>
        </div>
      )
    }

    return (
      <div className={classes.container} {...clickOutside}>
        {label && (
          <div className={css.label} onClick={() => onFocusHandle(true)}>
            {label}
          </div>
        )}

        <div className={classes.input}>
          {icon && (
            <div className={clsJoin(css.icon, classnames?.icon)}>{icon}</div>
          )}

          <input
            {...props}
            type={htmlType}
            onChange={(e) => onChangeHandle(e.target.value)}
            onKeyDown={(e) => onKeyDown(e)}
            value={value}
            ref={inputRef}
            placeholder={type === 'normal' ? placeholder : ''}
          />

          {success && (
            <div className={css.successIcon}>
              <IconInputCheck color="#18C4A9" />
            </div>
          )}

          {!success && buttonRight && (
            <div className={css.buttonRight}>{buttonRight}</div>
          )}

          {!success && (
            <Action
              can={can}
              value={value}
              size={size}
              onClear={() => {
                onChangeHandle('')

                onFocusHandle(true)

                onClear?.()
              }}
            />
          )}
        </div>

        {error && <div className={css.message}>{error}</div>}
      </div>
    )
  }
)

export default Input
